/**
 * SignatureCanvas — Touch/mouse signature drawing component
 */

import { useRef, useEffect, useState, useCallback } from 'react';

interface SignatureCanvasProps {
  onSave: (base64: string) => void;
  onClear: () => void;
}

export function SignatureCanvas({ onSave, onClear }: SignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  const getCoords = useCallback((e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in e) {
      const touch = e.touches[0]!;
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCoords(e);
    lastPoint.current = coords;

    const ctx = canvasRef.current!.getContext('2d')!;
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  }, [getCoords]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing || !lastPoint.current) return;

    const ctx = canvasRef.current!.getContext('2d')!;
    const coords = getCoords(e);

    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Smooth curve using quadratic bezier
    const midX = (lastPoint.current.x + coords.x) / 2;
    const midY = (lastPoint.current.y + coords.y) / 2;
    ctx.quadraticCurveTo(lastPoint.current.x, lastPoint.current.y, midX, midY);
    ctx.stroke();

    lastPoint.current = coords;
    setHasDrawn(true);
  }, [isDrawing, getCoords]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
    lastPoint.current = null;
    if (hasDrawn) {
      const dataUrl = canvasRef.current!.toDataURL('image/png');
      onSave(dataUrl);
    }
  }, [hasDrawn, onSave]);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onClear();
  }, [onClear]);

  // Set up canvas size
  useEffect(() => {
    const canvas = canvasRef.current!;
    const parent = canvas.parentElement!;
    canvas.width = parent.offsetWidth * 2; // 2x for retina
    canvas.height = 200;

    const ctx = canvas.getContext('2d')!;
    ctx.scale(1, 1);
  }, []);

  return (
    <div className="space-y-2">
      <div className="relative border border-gray-300 rounded-xl bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full touch-none cursor-crosshair"
          style={{ height: '100px' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasDrawn && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-gray-400">Teken hier uw handtekening</p>
          </div>
        )}
      </div>
      {hasDrawn && (
        <button
          type="button"
          onClick={clearCanvas}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Wissen en opnieuw tekenen
        </button>
      )}
    </div>
  );
}
